/* eslint-disable react-refresh/only-export-components */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/**
 * Panel IDs that can be opened from the sidebar rail.
 * Kept as a string so apps can introduce their own panel ids without
 * editing this file. (clario-frontend used a strict union here; we relax
 * it for the ecosystem so apps can extend.)
 */
export type RailPanelId = string;

interface SidebarContextType {
  activePanel: RailPanelId | null;
  isPanelOpen: boolean;
  lastPanel: RailPanelId | null;
  /** The panel for the current route, even while that panel is collapsed. */
  routePanel: RailPanelId | null;
  setRoutePanel: (panelId: RailPanelId | null) => void;
  /** True when the open panel was opened by hover (auto-closes on mouse leave). */
  openedByHover: boolean;
  togglePanel: (panelId: RailPanelId) => void;
  /** Open a panel explicitly (used by rail hover). `hover` marks it auto-closeable. */
  openPanel: (panelId: RailPanelId, options?: { hover?: boolean }) => void;
  closePanel: (options?: { forget?: boolean }) => void;
  /** Debounced close used by hover; closes only if the panel was hover-opened. */
  scheduleHoverClose: () => void;
  cancelHoverClose: () => void;
  clearLastPanel: () => void;
  isMobileOpen: boolean;
  toggleMobile: () => void;
  closeMobile: () => void;
  /** Pinned panel width in px, user-resizable and persisted across apps. */
  panelWidth: number;
  setPanelWidth: (width: number) => void;
  /** True while the panel's resize handle is being dragged. */
  isResizing: boolean;
  setIsResizing: (resizing: boolean) => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

const DEFAULT_SCOPE = 'default';
const OLD_STORAGE_KEY = 'clarix-sidebar-collapsed';
const LEGACY_PANEL_STORAGE_KEY = 'clarix-sidebar-panel';
const PANEL_STORAGE_PREFIX = 'clarix-sidebar-panel:';
const PANEL_WIDTH_STORAGE_KEY = 'clarix-sidebar-panel-width';

export const PANEL_DEFAULT_WIDTH = 224;
export const PANEL_MIN_WIDTH = 224;
export const PANEL_MAX_WIDTH = 480;

export function clampPanelWidth(width: number): number {
  return Math.round(Math.min(PANEL_MAX_WIDTH, Math.max(PANEL_MIN_WIDTH, width)));
}

function readStoredPanelWidth(): number {
  if (typeof window === 'undefined') return PANEL_DEFAULT_WIDTH;
  try {
    const stored = Number(localStorage.getItem(PANEL_WIDTH_STORAGE_KEY));
    return stored ? clampPanelWidth(stored) : PANEL_DEFAULT_WIDTH;
  } catch {
    return PANEL_DEFAULT_WIDTH;
  }
}

function panelKey(scope: string): string {
  return `${PANEL_STORAGE_PREFIX}${scope}`;
}

function readStoredPanel(scope: string): RailPanelId | null {
  if (typeof window === 'undefined') return null;
  try {
    return sessionStorage.getItem(panelKey(scope));
  } catch {
    return null;
  }
}

/**
 * Forget every app's pinned sidebar panel. Called on sign-out (useSignOut +
 * the shell's force-logout wipe) so a fresh login never resurrects the
 * previous session's chrome. Restore-on-reload within a session stays intact.
 */
export function clearPinnedSidebarPanel(): void {
  if (typeof window === 'undefined') return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(PANEL_STORAGE_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => sessionStorage.removeItem(key));
    localStorage.removeItem(LEGACY_PANEL_STORAGE_KEY);
  } catch {
    /* ignore quota / disabled storage */
  }
}

export function SidebarProvider({
  children,
  scope = DEFAULT_SCOPE,
}: {
  children: ReactNode;
  /** Isolates persisted panel state per app; e.g. the active app's id. */
  scope?: string;
}) {
  const [activePanel, setActivePanel] = useState<RailPanelId | null>(() => readStoredPanel(scope));
  const [isPanelOpen, setIsPanelOpen] = useState(() => readStoredPanel(scope) !== null);
  const [lastPanel, setLastPanel] = useState<RailPanelId | null>(null);
  const [routePanel, setRoutePanel] = useState<RailPanelId | null>(null);
  const [openedByHover, setOpenedByHover] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [panelWidth, setPanelWidthState] = useState(readStoredPanelWidth);
  const [isResizing, setIsResizing] = useState(false);

  const setPanelWidth = useCallback((width: number) => {
    setPanelWidthState(clampPanelWidth(width));
  }, []);

  // Persist on drag end only, not on every pointermove.
  useEffect(() => {
    if (isResizing || typeof window === 'undefined') return;
    try {
      localStorage.setItem(PANEL_WIDTH_STORAGE_KEY, String(panelWidth));
    } catch {
      /* ignore quota / private-mode failures */
    }
  }, [panelWidth, isResizing]);

  // Guarded adjust-during-render: AppShellLayout mounts one AppShell for every
  // app route, so the provider doesn't remount when the active app changes
  // within a tab. Re-reading in an effect would run in the same commit as the
  // persistence effect below and write the OLD app's panel under the NEW
  // app's key — so this must happen during render, not in a useEffect.
  const prevScopeRef = useRef(scope);
  if (prevScopeRef.current !== scope) {
    prevScopeRef.current = scope;
    const panel = readStoredPanel(scope);
    setActivePanel(panel);
    setIsPanelOpen(panel !== null);
    setLastPanel(null);
    setRoutePanel(null);
    setOpenedByHover(false);
  }

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      if (activePanel && isPanelOpen) {
        sessionStorage.setItem(panelKey(scope), activePanel);
      } else {
        sessionStorage.removeItem(panelKey(scope));
      }
    } catch {
      /* ignore quota / private-mode failures */
    }
  }, [activePanel, isPanelOpen, scope]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.removeItem(OLD_STORAGE_KEY);
      localStorage.removeItem(LEGACY_PANEL_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  const togglePanel = useCallback((panelId: RailPanelId) => {
    setOpenedByHover(false); // a click pins the panel open
    setActivePanel((prev) => {
      if (prev === panelId) {
        setIsPanelOpen((open) => {
          if (open) setLastPanel(panelId);
          else setLastPanel(null);
          return !open;
        });
        return prev;
      }
      setLastPanel(null);
      setIsPanelOpen(true);
      return panelId;
    });
  }, []);

  const openPanel = useCallback((panelId: RailPanelId, options?: { hover?: boolean }) => {
    setOpenedByHover(!!options?.hover);
    setLastPanel(null);
    setActivePanel(panelId);
    setIsPanelOpen(true);
  }, []);

  const closePanel = useCallback((options?: { forget?: boolean }) => {
    setOpenedByHover(false);
    setActivePanel((prev) => {
      if (prev && !options?.forget) setLastPanel(prev);
      else setLastPanel(null);
      return null;
    });
    setIsPanelOpen(false);
  }, []);

  const openedByHoverRef = useRef(false);
  useEffect(() => {
    openedByHoverRef.current = openedByHover;
  }, [openedByHover]);
  const hoverCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelHoverClose = useCallback(() => {
    if (hoverCloseTimer.current) {
      clearTimeout(hoverCloseTimer.current);
      hoverCloseTimer.current = null;
    }
  }, []);
  const scheduleHoverClose = useCallback(() => {
    cancelHoverClose();
    hoverCloseTimer.current = setTimeout(() => {
      if (openedByHoverRef.current) closePanel();
    }, 220);
  }, [cancelHoverClose, closePanel]);

  const clearLastPanel = useCallback(() => {
    setLastPanel(null);
  }, []);

  const toggleMobile = useCallback(() => {
    setIsMobileOpen((prev) => !prev);
  }, []);

  const closeMobile = useCallback(() => {
    setIsMobileOpen(false);
  }, []);

  return (
    <SidebarContext.Provider
      value={{
        activePanel,
        isPanelOpen,
        lastPanel,
        routePanel,
        setRoutePanel,
        openedByHover,
        togglePanel,
        openPanel,
        closePanel,
        scheduleHoverClose,
        cancelHoverClose,
        clearLastPanel,
        isMobileOpen,
        toggleMobile,
        closeMobile,
        panelWidth,
        setPanelWidth,
        isResizing,
        setIsResizing,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
}

export function useSidebar() {
  const ctx = useContext(SidebarContext);
  if (!ctx) {
    throw new Error('useSidebar must be used within SidebarProvider');
  }
  return ctx;
}

/**
 * Non-throwing variant — returns `undefined` when used OUTSIDE a
 * `SidebarProvider` (e.g. a remote app's standalone dev harness, unit tests).
 * Use this when a component may render both inside and outside the app shell
 * and only needs to read the sidebar state opportunistically.
 */
export function useSidebarOptional(): SidebarContextType | undefined {
  return useContext(SidebarContext);
}
